import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    List,
    ListItemButton,
    ListItemText,
    IconButton,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import AiSuggestionPanel from '../ai-suggestion-panel';
import {
    generateSuggestions as generateAiSuggestionsRequest,
    recordOutcome as recordAiSuggestionOutcomeRequest,
    getAvailability as getAiSuggestionAvailabilityRequest,
} from '../../services/aisuggestion.service';

const resolveAiSuggestionErrorType = (errorMessage) => {
    if (!errorMessage || typeof errorMessage !== 'string') return 'generic';
    const normalized = errorMessage.toLowerCase();
    if (normalized.includes('limite di')) return 'limit';
    if (normalized.includes('não está habilitada') || normalized.includes('nao esta habilitada')) return 'not_enabled';
    if (normalized.includes('não está disponível') || normalized.includes('nao esta disponivel') || normalized.includes('não foi possível gerar') || normalized.includes('nao foi possivel gerar')) return 'unavailable';
    return 'generic';
};

const AiSuggestionTrigger = ({
    calledId = null,
    chatId = null,
    buildFieldChanges = () => [],
    onUseSuggestion,
    onCopySuggestion,
    iconSx,
    iconSize,
    outdated = false,
    onRegenerate,
    hideTrigger = false,
    open = false,
    anchorEl: controlledAnchorEl = null,
    onClose,
    onAvailabilityChange,
}) => {
    const intl = useIntl();

    const [availabilityChecking, setAvailabilityChecking] = useState(true);
    const [available, setAvailable] = useState(false);
    const [llms, setLlms] = useState([]);
    const [reloadingAvailability, setReloadingAvailability] = useState(false);
    const [reloadStatus, setReloadStatus] = useState(null);
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const [internalAnchorEl, setInternalAnchorEl] = useState(null);
    const [llmDialogOpen, setLlmDialogOpen] = useState(false);
    const [panelOpen, setPanelOpen] = useState(false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [errorType, setErrorType] = useState(null);
    const [suggestions, setSuggestions] = useState([]);

    const activeLlmIdRef = useRef(null);

    useEffect(() => {
        let isMounted = true;

        const checkAvailability = async () => {
            const result = await getAiSuggestionAvailabilityRequest();
            if (!isMounted) return;
            const data = result && result.success ? result.data : null;
            setAvailable(Boolean(data && data.available));
            setLlms((data && data.llms) || []);
            setAvailabilityChecking(false);
        };

        checkAvailability();

        return () => {
            isMounted = false;
        };
    }, []);

    const handleReloadAvailability = async (event) => {
        event.stopPropagation();
        event.preventDefault();

        if (reloadingAvailability) return;

        setReloadingAvailability(true);
        setReloadStatus(null);

        const result = await getAiSuggestionAvailabilityRequest(true);
        const data = result && result.success ? result.data : null;
        const nowAvailable = Boolean(data && data.available);

        setAvailable(nowAvailable);
        setLlms((data && data.llms) || []);
        setReloadStatus(nowAvailable ? null : 'unavailable');
        setReloadingAvailability(false);
    };

    const handleTooltipOpen = () => setTooltipOpen(true);
    const handleTooltipClose = () => {
        if (reloadingAvailability) return;
        setTooltipOpen(false);
        setReloadStatus(null);
    };

    useEffect(() => {
        onAvailabilityChange?.({ available, checking: availabilityChecking });
    }, [available, availabilityChecking]);

    useEffect(() => {
        if (!hideTrigger) return;
        setPanelOpen(Boolean(open));
    }, [open, hideTrigger]);

    const effectiveAnchorEl = hideTrigger ? controlledAnchorEl : internalAnchorEl;

    const togglePanel = (event) => {
        if (panelOpen) {
            closePanel();
            return;
        }
        setInternalAnchorEl(event.currentTarget);
        setPanelOpen(true);
    };
    const closePanel = () => {
        setPanelOpen(false);
        if (hideTrigger) {
            onClose?.();
        }
    };
    const closeLlmDialog = () => setLlmDialogOpen(false);

    const runGeneration = async (regenerate, llmId) => {
        setLoading(true);
        setError(false);
        setErrorType(null);

        const fieldChanges = buildFieldChanges() || [];

        const payload = {
            calledId: calledId || null,
            chatId: chatId || null,
            fieldChanges,
            language: intl.locale,
            regenerate: regenerate === true,
            ignoreCache: fieldChanges.length > 0,
        };
        if (llmId != null) {
            payload.llmId = llmId;
        }

        const result = await generateAiSuggestionsRequest(payload);

        if (result && result.success && result.data) {
            setLoading(false);
            setError(false);
            setErrorType(null);
            setSuggestions(result.data.suggestions || []);
            onRegenerate?.();
        } else {
            setLoading(false);
            setError(true);
            setErrorType(resolveAiSuggestionErrorType(result && result.error));
            setSuggestions([]);
        }
    };

    const handleGenerateClick = () => {
        if (llms.length > 1) {
            setLlmDialogOpen(true);
            return;
        }

        const singleLlmId = llms.length === 1 ? llms[0].id : null;
        activeLlmIdRef.current = singleLlmId;
        runGeneration(false, singleLlmId);
    };

    const handleSelectLlm = (llm) => {
        closeLlmDialog();
        activeLlmIdRef.current = llm.id;
        runGeneration(false, llm.id);
    };

    const handleRegenerate = async () => {
        await runGeneration(true, activeLlmIdRef.current);
    };

    const handleDiscard = (suggestion) => {
        if (!suggestion) return;
        setSuggestions((prevList) => prevList.filter((item) => item.id !== suggestion.id));
        recordAiSuggestionOutcomeRequest({ suggestionId: suggestion.id, outcome: 'discarded' });
    };

    const handleUse = (suggestion) => {
        closePanel();
        onUseSuggestion?.(suggestion);
    };

    const handleCopy = (suggestion) => {
        if (!suggestion) return;
        onCopySuggestion?.(suggestion);
    };

    const isOpen = panelOpen;
    const isDisabled = availabilityChecking || !available;

    const disabledMessageId = reloadStatus === 'unavailable'
        ? 'ai.suggestion.trigger.reload.still.unavailable'
        : 'ai.suggestion.trigger.disabled.tooltip';

    const tooltipTitle = isDisabled
        ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <h5 style={{ margin: 0 }}>{intl.formatMessage({ id: disabledMessageId })}</h5>
                <IconButton
                    aria-label={intl.formatMessage({ id: 'ai.suggestion.trigger.reload' })}
                    size="small"
                    onClick={handleReloadAvailability}
                    disabled={reloadingAvailability}
                    sx={{ color: 'inherit' }}
                >
                    {reloadingAvailability
                        ? <CircularProgress size={16} color="inherit" />
                        : <RefreshIcon fontSize="small" />}
                </IconButton>
            </span>
        )
        : <h5>{intl.formatMessage({ id: 'ai.suggestion.action.label' })}</h5>;

    return (
        <React.Fragment>
            {!hideTrigger && (
                <React.Fragment>
                    <Tooltip
                        title={tooltipTitle}
                        placement="right"
                        arrow
                        disableHoverListener={isOpen}
                        open={tooltipOpen}
                        onOpen={handleTooltipOpen}
                        onClose={handleTooltipClose}
                    >
                        <span>
                            <IconButton
                                aria-label="ai-suggestion"
                                aria-haspopup="true"
                                aria-expanded={isOpen ? 'true' : 'false'}
                                onClick={togglePanel}
                                disabled={isDisabled}
                                size={iconSize}
                                className="my-mexx-icon"
                                sx={{
                                    cursor: 'pointer',
                                    color: isOpen ? 'primary.main' : 'inherit',
                                    backgroundColor: isOpen ? 'action.selected' : 'transparent',
                                    '&:hover': { backgroundColor: 'action.hover' },
                                    '&&&': { ...iconSx },
                                    '&&&:hover': { ...iconSx },
                                }}
                            >
                                <SmartToyOutlinedIcon style={{ fontSize: '2rem' }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </React.Fragment>
            )}

            <Dialog open={llmDialogOpen} onClose={closeLlmDialog} fullWidth maxWidth="xs">
                <DialogTitle>{intl.formatMessage({ id: 'ai.suggestion.select.llm.title' })}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 1 }}>
                        {intl.formatMessage({ id: 'ai.suggestion.select.llm.description' })}
                    </DialogContentText>
                    <List>
                        {llms.map((llm) => (
                            <ListItemButton key={llm.id} onClick={() => handleSelectLlm(llm)}>
                                <ListItemText primary={llm.name} />
                            </ListItemButton>
                        ))}
                    </List>
                </DialogContent>
            </Dialog>

            <AiSuggestionPanel
                open={Boolean(panelOpen && effectiveAnchorEl)}
                anchorEl={effectiveAnchorEl}
                onClose={closePanel}
                loading={loading}
                error={error}
                errorType={errorType}
                suggestions={suggestions}
                outdated={outdated}
                onUse={handleUse}
                onCopy={handleCopy}
                onDiscard={handleDiscard}
                onRegenerate={handleRegenerate}
                onGenerate={handleGenerateClick}
            />
        </React.Fragment>
    );
};

export default AiSuggestionTrigger;
